import { isEVMChain } from '@particle-network/connector-core';
import { Fragment, useMemo } from 'react';
import Forward from '../../assets/icons/Forward';
import ToggleIcon from '../../assets/toggle';
import { useContext } from '../../context';
import { useAppearance } from '../../hooks/useAppearance';
import { useConnectWithWallet } from '../../hooks/useConnectWithWallet';
import { useLastConnectorId } from '../../hooks/useLastConnectorId';
import useLocales from '../../hooks/useLocales';
import { useRouter } from '../../hooks/useRouter';
import { useWalletConnector } from '../../hooks/useWalletConnectors';
import { useWalletProps } from '../../hooks/useWalletsProps';
import { RoutePath } from '../../pages';
import { isSocialAuthType } from '../../utils/social';
import { ForwardContainer, WalletLable } from '../wallets/styles';
import SocialIcon from './Icons';
import { SocialItem, SocialLoginContainer, ToggleContainer } from './styles';

const SocialLogin = ({ expandList }: { expandList?: boolean }) => {
  const { config } = useContext();
  const { navigate } = useRouter();
  const locales = useLocales();
  const { appearance } = useAppearance();
  const wallet = useWalletProps(isEVMChain(config.chains[0]) ? 'particleEVM' : 'particleSolana');
  const walletConnector = useWalletConnector('particleAuth');
  const evmWalletConnector = useWalletConnector('evmWallet');

  const { lastConnectorId } = useLastConnectorId();

  const connectWallet = useConnectWithWallet();

  const recentConnectSocialAuthType = useMemo(() => {
    if (lastConnectorId !== 'particleSolana' && lastConnectorId !== 'particleEVM') {
      return lastConnectorId;
    }

    const lastAuthType = walletConnector.auth.getLatestAuthType();
    return lastAuthType;
  }, [lastConnectorId, walletConnector.auth]);

  const authTypes = useMemo(() => {
    if (!walletConnector) {
      return [];
    }
    const hasPasskeyConnector = evmWalletConnector?.connectors.some(
      (connector) => connector.id === 'passkeySmartWallet'
    );
    const enablePasskey =
      Boolean(config.plugins.some((plugin) => plugin.id === 'aa') && hasPasskeyConnector) &&
      appearance.collapsePasskeyButton === 'as-social';
    if (enablePasskey) {
      return walletConnector.supportAuthTypes.concat('passkeySmartWallet');
    }
    return walletConnector.supportAuthTypes;
  }, [walletConnector, evmWalletConnector, appearance.collapsePasskeyButton, config.plugins]);

  const socialAuthTypes = useMemo(() => {
    return authTypes.filter((item) => isSocialAuthType(item) || item === 'passkeySmartWallet');
  }, [authTypes]);

  const _expandList = useMemo(() => {
    const evmConnector = config.getWalletConnector('evmWallet');
    const solanaConnector = config.getWalletConnector('solanaWallet');
    return (
      expandList || (!authTypes.includes('email') && !authTypes.includes('phone') && !evmConnector && !solanaConnector)
    );
  }, [expandList, authTypes, config]);

  const renderAuthTypes = useMemo(() => {
    if (_expandList) {
      return socialAuthTypes;
    }

    if (socialAuthTypes.length > 5) {
      return socialAuthTypes.slice(0, 4);
    }
    return socialAuthTypes;
  }, [_expandList, socialAuthTypes]);

  const onSocialLogin = (authType: string) => {
    if (wallet && isSocialAuthType(authType)) {
      const name = authType.charAt(0).toUpperCase() + authType.slice(1);
      connectWallet(
        {
          ...wallet,
          name,
          shortName: name,
          iconShape: authType === 'microsoft' || authType === 'twitch' ? 'squircle' : 'circle',
          icon: <SocialIcon authType={authType} />,
        },
        {
          authParams: {
            socialType: authType,
            prompt: 'select_account',
          },
        }
      );
    } else if (authType === 'passkeySmartWallet') {
      navigate(RoutePath.LoginPasskey);
    }
  };

  const felxRow = useMemo(() => {
    if (_expandList) {
      return false;
    }
    return socialAuthTypes.length > 3;
  }, [_expandList, socialAuthTypes]);

  return (
    <SocialLoginContainer $felxRow={felxRow}>
      {renderAuthTypes.map((authType, index) => {
        return (
          <Fragment key={authType}>
            <SocialItem $felxRow={felxRow} onClick={() => onSocialLogin(authType)}>
              <SocialIcon authType={authType} />
              <span>{authType === 'passkeySmartWallet' ? 'Passkey' : authType}</span>
              {!felxRow && (
                <>
                  {recentConnectSocialAuthType === authType && <WalletLable>{locales.lastUsed}</WalletLable>}
                  <ForwardContainer>
                    <Forward />
                  </ForwardContainer>
                </>
              )}
            </SocialItem>
            {!_expandList && index == 3 && socialAuthTypes.length > 5 && (
              <ToggleContainer onClick={() => navigate(RoutePath.Connectors, { type: 'social' })}>
                <ToggleIcon />
              </ToggleContainer>
            )}
          </Fragment>
        );
      })}
    </SocialLoginContainer>
  );
};

export default SocialLogin;
